<template>
    <DCard padding>
        <ArchiveModeSwitch
            class="mb-3"
            :model-value="archiveMode"
            :show-type-select="false"
            :reference-id="filterData.archiveContractId || ''"
            :reference-id-error="errors.archiveContractId"
            :read-failed="archiveReadFailed"
            @update:modelValue="onArchiveToggle"
            @update:referenceId="(value) => { filterData.archiveContractId = value || null }"
            @apply="applyArchiveFilters"
        />
        <div
            v-if="!archiveMode"
            class="search"
        >
            <DRow
                align-horizontal="start"
                align-vertical="end"
                class="mb-3"
            >
                <DCol sm="2">
                    <DDateFormatInput
                        id="date-from"
                        v-model="filterData.dateFrom"
                        format="YYYY-MM-DD"
                        type="date"
                        :label="__('contracts.domains.date_from')"
                        @keydown.enter.exact.prevent="filter"
                    />
                </DCol>
                <DCol sm="2">
                    <DDateFormatInput
                        id="date-to"
                        v-model="filterData.dateTo"
                        format="YYYY-MM-DD"
                        type="date"
                        :label="__('contracts.domains.date_to')"
                        @keydown.enter.exact.prevent="filter"
                    />
                </DCol>
                <DCol sm="2">
                    <DMultiSelect
                        id="product-groups-dropdown"
                        :options="moduleOptions"
                        :search="true"
                        :selection="true"
                        :selected="filterData.modules"
                        :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                        selection-type="text"
                        :label="__('contracts.domains.module')"
                        @update="updateSelectedModules"
                    />
                </DCol>
                <DCol sm="2">
                    <DMultiSelect
                        id="products-dropdown"
                        :options="actionOptions"
                        :search="true"
                        :selection="true"
                        :selected="filterData.actions"
                        :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                        selection-type="text"
                        :label="__('contracts.domains.action')"
                        @update="updateSelectedActions"
                    />
                </DCol>
                <DCol sm="2">
                    <DMultiSelect
                        id="status-dropdown"
                        :options="statusOptions"
                        :search="true"
                        :selection="true"
                        :selected="filterData.status"
                        :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                        selection-type="text"
                        :label="__('contracts.domains.status')"
                        @update="updateSelectedStatus"
                    />
                </DCol>
                <DCol sm="2">
                    <DMultiSelect
                        id="segments-dropdown"
                        :options="subStatusOptions"
                        :search="true"
                        :selection="true"
                        :selected="filterData.subStatus"
                        :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                        selection-type="text"
                        :label="__('contracts.domains.substatus')"
                        @update="updateSelectedSubStatus"
                    />
                </DCol>
            </DRow>
            <DRow
                align-horizontal="start"
                align-vertical="end"
                class="mb-3"
            >
                <DCol sm="2">
                    <DInput
                        id="date-to"
                        v-model="filterData.tld"
                        :label="__('contracts.domains.tld')"
                        type="input"
                        @keydown.enter.exact.prevent="filter"
                    />
                </DCol>
                <DCol sm="2">
                    <DInput
                        id="customer-id"
                        v-model="filterData.customerId"
                        :label="__('contracts.domains.customer')"
                        type="input"
                        @keydown.enter.exact.prevent="filter"
                    />
                </DCol>
                <DCol sm="2">
                    <DInput
                        id="customer-id"
                        v-model="filterData.contractId"
                        :label="__('contracts.domains.contract')"
                        type="input"
                        @keydown.enter.exact.prevent="filter"
                    />
                </DCol>
            </DRow>
            <DRow class="mb-2">
                <DCol
                    sm="3"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('messages.actions.filter')"
                        @click="filter"
                    />
                    <DButton
                        id="filter-reset"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
        </div>

        <div
            v-if="requests"
            class="absolute-checkbox"
        />
        <DRow>
            <DCol lg="12">
                <!--
                    One table for both modes: archive mode drops the sorter, the selection column
                    and the row actions, but every cell renders through the same templates. The
                    sorter is off rather than inert, since the archive has one fixed ordering.

                    `archiveFields` drops the `checkbox` column, leaving its two slots below with
                    no column to render into -- but DTable captures the slot set once in setup and
                    teleports `checkbox-header` into `tr:nth-child(1) th:nth-child(1)` by CSS
                    selector. Without the `v-if` and the `key` the select-all box stays teleported
                    into what is now the first data column's header.

                    `archiveItems` follows the server `archive` prop rather than the toggle, so
                    the list is empty until Apply.
                -->
                <DTable
                    :key="archiveMode ? 'archive' : 'live'"
                    :items="archiveMode ? archiveItems : requests.items"
                    :fields="archiveMode ? archiveFields : fields"
                    :sorter="archiveMode ? false : isSortable"
                    :sorter-value="archiveMode ? undefined : sorterValue"
                    :no-items-label="archiveMode ? archiveNoItemsLabel : undefined"
                    @update:sorter-value="updateOrderValue"
                >
                    <template
                        v-if="!archiveMode"
                        #checkbox-header
                    >
                        <CFormCheck
                            v-if="requests.items.length"
                            id="absolute-checkbox-id"
                            v-model:checked="allItems"
                            @change="updateAllCheckboxes"
                        />
                    </template>
                    <template #checkbox="{item}">
                        <td>
                            <CFormCheck
                                v-if="item.request.domain_trade_request.status !== 'complete'"
                                :id="'id-' + item.request.id"
                                :checked="selectedRequests.includes(item.request.id)"
                                @change="updateCheckboxArray(item.request.id)"
                            />
                        </td>
                    </template>
                    <template #customer="{item}">
                        <td v-if="userPermissions.includes('customer.view')">
                            <Link :href="customerProfileRoute(item.customer.id)">
                                <span v-if="item.customer.company">{{ item.customer.company }}</span>
                                <br v-if="item.customer.company">
                                <span v-if="item.customer.firstname">
                                    {{ item.customer.firstname }}
                                </span>
                                <span v-if="item.customer.lastname">
                                    {{ item.customer.lastname }}
                                </span>
                                <span>({{ item.customer.id }})</span>
                            </Link>
                        </td>
                        <td v-else>
                            <span v-if="item.customer.company">{{ item.customer.company }}</span>
                            <br v-if="item.customer.company">
                            <span v-if="item.customer.firstname">
                                {{ item.customer.firstname }}
                            </span>
                            <span v-if="item.customer.lastname">
                                {{ item.customer.lastname }}
                            </span>
                            <span>({{ item.customer.id }})</span>
                        </td>
                    </template>

                    <template #contract="{item}">
                        <td v-if="userPermissions.includes('contract.view')">
                            <Link :href="contractRoute(item.customer.id, item.request.job_reference.id)">
                                {{ externalId(item.request.job_reference.external_id) + ' (' + item.request.job_reference.id + ')' }}
                            </Link>
                        </td>
                        <td v-else>
                            {{ externalId(item.request.job_reference.external_id) + ' (' + item.request.job_reference.id + ')' }}
                        </td>
                    </template>

                    <template #since="{item}">
                        <td v-if="item.status === 'pending'">
                            {{ item.since }}
                        </td>
                        <td v-else />
                    </template>

                    <template #updated_at="{item}">
                        <td>
                            {{ setDateFormat(item.updated_at) }}
                        </td>
                    </template>

                    <template #tld="{item}">
                        <td v-if="item.tld">
                            {{ item.tld }}
                        </td>
                        <td v-else />
                    </template>

                    <template #subStatus="{item}">
                        <td v-if="item.subStatus">
                            {{ item.subStatus }}
                        </td>
                        <td v-else />
                    </template>

                    <template #actions="{item}">
                        <td style="display: flex; grid-gap: 5px;">
                            <DButton
                                icon="eye"
                                @click="onRowClicked(item)"
                            />
                            <!-- No live row left to check, approve, reject or cancel. -->
                            <DDropdown
                                v-if="!archiveMode && item.status !== 'complete'"
                                :id="`actions_dropdown_${item.reference_id}`"
                                :class="'more-actions-button'"
                                :caret="false"
                                icon="dots-horizontal"
                            >
                                <DDropdownItem
                                    v-if="item.action === 'trade_domain' && item.status !== 'approval' && item.status !== 'email_verification'"
                                    :id="`action_resend_email_${item.reference_id}`"
                                    @click="handleActionClick(item, 'resend_domain_trade_email')"
                                >
                                    {{ __('contracts.domains.resend_email') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    v-if="item.status !== 'approval' && item.status !== 'email_verification'"
                                    :id="`action_check_status_${item.reference_id}`"
                                    @click="handleActionClick(item, item.action === 'trade_domain' ? 'check_domain_trade_status' : 'check_owner_data_update_status')"
                                >
                                    {{ __('contracts.domains.check_status') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    v-if="item.action === 'trade_domain' && item.status !== 'approval' && item.status !== 'email_verification'"
                                    :id="`action_cancel_${item.reference_id}`"
                                    @click="handleActionClick(item, 'cancel_domain_trade')"
                                >
                                    {{ __('contracts.domains.cancel_trade') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    v-if="item.action === 'trade_domain' && item.request.domain_trade_request.status !== 'complete' && item.status !== 'email_verification'"
                                    :id="`action_mark_as_complete_${item.reference_id}`"
                                    @click="markTradeAsComplete(item)"
                                >
                                    {{ __('contracts.domains.mark_as_complete') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    v-if="item.status === 'approval'"
                                    :id="`action_approve_${item.reference_id}`"
                                    @click="handleApprove(item)"
                                >
                                    {{ __('tools.fulfillment.approve') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    v-if="item.status === 'email_verification'"
                                    :id="`action_approve_${item.reference_id}`"
                                    @click="checkEmailVerificationStatus(item)"
                                >
                                    {{ __('tools.fulfillment.email_verification') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    v-if="item.status === 'approval'"
                                    :id="`action_reject_${item.reference_id}`"
                                    @click="handleReject(item)"
                                >
                                    {{ __('tools.fulfillment.reject') }}
                                </DDropdownItem>
                            </DDropdown>
                        </td>
                    </template>
                </DTable>

                <DPagination
                    v-if="paginationVisible"
                    :active-page="requests.current_page"
                    :pages="requests.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
        <DomainRequestDetailsModal
            v-if="showModal"
            :show-modal="showModal"
            :request="requestForDetails"
            @close-modal="closeModal"
        />
        <DFabs
            v-if="!archiveMode && selectedRequests.length > 0"
        >
            <DFab
                icon="clipboard-account"
                :label="__('contracts.domains.check_status')"
                @click="handleAction('check_domain_trade_status')"
            />
            <DFab
                icon="email"
                :label="__('contracts.domains.resend_email')"
                @click="handleAction('resend_domain_trade_email')"
            />
            <DFab
                icon="close"
                :label="__('contracts.domains.cancel_trade')"
                @click="handleAction('cancel_domain_trade')"
            />
        </DFabs>
    </DCard>
</template>

<script>

import _ from 'lodash'
import ArchiveModeSwitch from '~@/Components/Common/Archive/ArchiveModeSwitch.vue'
import DomainRequestDetailsModal from '~@/Components/Contracts/Domains/RequestDetailsModal.vue'

export default {
    name: 'ContractsIndex',
    components: {
        ArchiveModeSwitch,
        DomainRequestDetailsModal
    },
    props: {
        requests: {
            type: Object,
            default: () => { return {} }
        },
        modules: {
            type: Array,
            default () {
                return []
            }
        },
        actions: {
            type: Array,
            default () {
                return []
            }
        },
        status: {
            type: Array,
            default () {
                return []
            }
        },
        subStatus: {
            type: Array,
            default () {
                return []
            }
        },
        filters: {
            type: Object,
            default () {
                return {}
            }
        },
        orderValues: {
            type: Object,
            default: () => {
                return {}
            }
        },
        archive: {
            type: Boolean,
            default: false
        },
        archiveContractId: {
            type: [Number, String],
            default: null
        },
        archiveReadFailed: {
            type: Boolean,
            default: false
        },
        errors: {
            type: Object,
            default: () => { return {} }
        }
    },
    data () {
        return {
            fields: [
                { key: 'checkbox', label: '' },
                { key: 'updated_at', sorter: true, label: this.__('contracts.domains.updated_at') },
                { key: 'module', sorter: true, label: this.__('contracts.domains.module') },
                { key: 'action', sorter: true, label: this.__('contracts.domains.action') },
                { key: 'status', sorter: true, label: this.__('contracts.domains.status') },
                { key: 'subStatus', sorter: true, label: this.__('contracts.domains.substatus') },
                { key: 'tld', sorter: true, label: this.__('contracts.domains.tld') },
                { key: 'customer', sorter: true, label: this.__('contracts.domains.customer') },
                { key: 'contract', sorter: true, label: this.__('contracts.domains.contract') },
                { key: 'since', sorter: true, label: this.__('contracts.domains.since') },
                { key: 'actions', sorter: false, label: this.__('contracts.domains.actions') }
            ],
            // The archive lookup rides the live filter object, since visits send it wholesale,
            // but under its own key: sharing `contractId` left each mode filtering the other's
            // list. Seeded from the prop so a toggle or refresh keeps it, and never saved,
            // because DomainContractsFilter::toArray() emits only the live keys.
            filterData: { ..._.cloneDeep(this.filters), archiveContractId: this.archiveContractId ?? '' },
            // Local, not the `archive` prop -- see ArchiveModeSwitch. A rejected lookup also
            // counts as on: validation redirects to a URL with no `archive`, so the switch would
            // otherwise snap back to live and take the error message off screen with it.
            archiveMode: this.archive || Boolean(this.errors.archiveContractId),
            productsOptions: [],
            requestForDetails: {},
            showModal: false,
            selectedRequests: [],
            allItems: false,
            orderBy: this.orderValues.orderBy ?? '',
            orderByAsc: this.orderValues.orderByAsc ?? 0
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        sorterValue () {
            return {
                column: this.orderValues.orderBy,
                asc: !!this.orderValues.orderByAsc
            }
        },
        isSortable () {
            if (!this.sorterValue.column) return false

            return { external: true }
        },
        // Same gate as the rows: until an archive visit happens the props still hold the live
        // result set, so a live page count would sit under an empty archive table.
        paginationVisible () {
            if (this.archiveMode && !this.archive) {
                return false
            }

            return this.requests.last_page > 1
        },
        // Empty until the server has actually rendered archive mode -- see the table comment.
        archiveItems () {
            return this.archive ? this.requests.items : []
        },
        archiveNoItemsLabel () {
            return this.archive && this.filterData.archiveContractId
                ? this.__('messages.archive.no_records')
                : this.__('messages.archive.contract_hint')
        },
        // The same data columns, minus the selection column and with every sorter cleared.
        archiveFields () {
            return this.fields
                .filter((field) => field.key !== 'checkbox')
                .map((field) => ({ ...field, sorter: false }))
        },
        moduleOptions () {
            return this.modules.map((item) => {
                return {
                    value: item,
                    text: item,
                    selected: this.filterData?.modules?.includes(String(item))
                }
            })
        },
        statusOptions () {
            return this.status.map((item) => {
                return {
                    value: item.value,
                    text: item.text,
                    selected: this.filterData?.status?.includes(String(item.value))
                }
            })
        },
        subStatusOptions () {
            return this.subStatus.map((item) => {
                return {
                    value: item.value,
                    text: item.text,
                    selected: this.filterData?.subStatus?.includes(String(item.value))
                }
            })
        },
        actionOptions () {
            return this.actions.map((item) => {
                return {
                    value: item.value,
                    text: item.text,
                    selected: this.filterData?.actions?.includes(String(item.value))
                }
            })
        }
    },
    methods: {
        customerProfileRoute (customerId) {
            return this.$route('customers.customers.show', { customer: customerId })
        },
        contractRoute (customerId, contractId) {
            return this.$route('customers.customers.contracts.show', { customer: customerId, contract: contractId })
        },
        contractShowRoute (customerId, contractId) {
            return this.$inertia.get(this.contractRoute(customerId, contractId))
        },
        updateSelectedActions (values) {
            this.filterData.actions = values
        },
        updateSelectedModules (values) {
            this.filterData.modules = values
        },
        updateSelectedStatus (values) {
            this.filterData.status = values
        },
        updateSelectedSubStatus (values) {
            this.filterData.subStatus = values
        },
        changePage (page) {
            if (this.archive) {
                const query = {}

                query.page = page
                // This method carries no other filters, so these two have to travel or paging
                // lands back on the empty state. No sort: the archive order is fixed.
                //
                // 1, not true: this goes through Inertia's `data`, which qs.stringify's to
                // `archive=true`, a string Laravel's `boolean` rule rejects -- the page turn
                // would silently do nothing. Same reason the live branch casts `orderByAsc`.
                query.archive = 1
                query.archiveContractId = this.filterData.archiveContractId

                this.$inertia.get(this.$route('contracts.domains'), query)

                return
            }

            const query = {
                modules: this.filterData.modules,
                actions: this.filterData.actions,
                status: this.filterData.status,
                subStatus: this.filterData.subStatus,
                customerId: this.filterData.customerId,
                contractId: this.filterData.contractId,
                tld: this.filterData.tld,
                dateFrom: this.filterData.dateFrom,
                dateTo: this.filterData.dateTo,
                orderBy: this.orderBy,
                orderByAsc: +this.orderByAsc,
                page: page
            }

            this.$inertia.get(this.$route('contracts.domains', { save_filters: true }), query)
        },
        filter () {
            this.filterData.reset = false
            this.$inertia.get(this.$route('contracts.domains', {
                _query: this.filterData,
                save_filters: true
            }))
        },
        resetFilters () {
            this.$inertia.get(this.$route('contracts.domains', {
                _query: { reset: true },
                save_filters: true,
                default_filters: true
            }))
        },
        onRowClicked (item) {
            this.requestForDetails = item.request
            this.showModal = true
        },
        // On reads nothing yet -- the switch shows the hint until the contract id is set.
        // Off goes straight back to live.
        onArchiveToggle (value) {
            this.archiveMode = value

            if (!value) {
                // Ziggy skips null query values, so this leaves the live URL exactly as it was.
                this.filterData.archive = null
                this.filterData.archiveContractId = null
                this.filterData.reset = false
                this.$inertia.get(this.$route('contracts.domains', { _query: this.filterData }))

                return
            }

            this.filterData.archive = true
        },
        applyArchiveFilters () {
            this.$inertia.get(this.$route('contracts.domains', {
                _query: {
                    archive: true,
                    archiveContractId: this.filterData.archiveContractId
                }
            }))
        },
        closeModal () {
            this.requestForDetails = null
            this.showModal = false
        },
        shouldRenderModal () {
            return this.showModal && Object.keys(this.requestForDetails).length > 0
        },
        handleActionClick (item, action) {
            this.selectedRequests.push(item.request.id)
            this.handleAction(action)
        },
        handleAction (action) {
            this.$inertia.get(this.$route('contracts.domains.handle-action', { requests: this.selectedRequests, action: action }))
            this.selectedRequests = []
        },
        markTradeAsComplete (item) {
            this.$inertia.post(this.$route('contracts.domains.mark-trade-as-complete', { jobRequest: item.request.id }))
        },
        handleApprove (item) {
            this.$inertia.post(this.$route('tools.fulfillment.approve-domain-trade-request', { jobRequest: item.request.id }))
        },
        checkEmailVerificationStatus (item) {
            this.$inertia.post(this.$route('tools.fulfillment.check-email-verification-status', { jobRequest: item.request.id }))
        },
        handleReject (item) {
            this.$inertia.post(this.$route('tools.fulfillment.reject-domain-trade-request', { jobRequest: item.request.id }))
        },
        updateCheckboxArray (item, allowRemoving = true) {
            if (this.selectedRequests.includes(item) && allowRemoving) {
                this.selectedRequests = this.selectedRequests.filter(value => value !== item)
            } else if (!this.selectedRequests.includes(item)) {
                this.selectedRequests.push(item)
            }
        },
        updateAllCheckboxes () {
            Object.keys(this.requests.items).forEach(key => {
                this.updateCheckboxArray(this.requests.items[key].request.id, !this.allItems)
            })
        },
        applyFilters () {
            return this.$inertia.visit(this.$route('contracts.domains',
                {
                    modules: this.filterData.modules,
                    actions: this.filterData.actions,
                    status: this.filterData.status,
                    subStatus: this.filterData.subStatus,
                    customerId: this.filterData.customerId,
                    contractId: this.filterData.contractId,
                    // Carried so a live filter or sort does not discard the archive lookup the
                    // switch shows again on the way back. Ziggy drops it when null.
                    archiveContractId: this.filterData.archiveContractId,
                    tld: this.filterData.tld,
                    dateFrom: this.filterData.dateFrom,
                    dateTo: this.filterData.dateTo,
                    orderBy: this.orderBy,
                    orderByAsc: this.orderByAsc
                }))
        },
        updateOrderValue (event) {
            this.orderByAsc = event.asc ? 1 : 0
            this.orderBy = event.column
            this.applyFilters()
        },
        externalId (externalId) {
            return externalId !== null ? externalId : ''
        }
    }
}
</script>
